// apps/web/src/features/home/components/SlideOver.jsx
import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";

export default function SlideOver({
  isOpen,
  onClose,
  children,
  side = "left", 
  width,
  backdropClosable = true,
  widths = {
    mobile: "75vw",
    tablet: "60vw",
    desktop: "380px",
    large: "420px",
  },
}) {
  const [w, setW] = useState(() => getWidth(widths));
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);

  // Lock scroll + Esc to close
  useEffect(() => {
    if (!shouldRender) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [shouldRender, onClose]);

  // Update width on resize
  useEffect(() => {
    if (!shouldRender) return;
    const onResize = () => setW(getWidth(widths));
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, [shouldRender, widths]);

  // Manage component lifecycle and transitions
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Wait for a tick to apply the "open" state and trigger the transition
      const timeout = setTimeout(() => setIsTransitioning(true), 10);
      return () => clearTimeout(timeout);
    } else {
      setIsTransitioning(false);
    }
  }, [isOpen]);

  // Cleanup after transition ends
  const handleTransitionEnd = (e) => {
    // Only unmount if the transform animation has finished and the panel is closing
    if (e.propertyName === 'transform' && !isTransitioning) {
      setShouldRender(false);
    }
  };

  if (!shouldRender) return null;

  const resolvedWidth = width || w;

  const panelCommon = {
    position: "absolute",
    top: 0,
    bottom: 0,
    height: "100vh",
    width: resolvedWidth,
    maxWidth: "100vw",
    background: "#fff",
    boxShadow:
      side === "left"
        ? "6px 0 24px rgba(0,0,0,.12)" // This seems backwards in your original code, should be positive for right, negative for left
        : "-6px 0 24px rgba(0,0,0,.12)",
    display: "flex",
    flexDirection: "column",
    transition: 'transform 0.6s ease-out', // Added transition property
  };

  // Calculate the transform value based on side and transition state
  const transformStyle =
    side === "left"
      ? `translateX(${isTransitioning ? "0" : "-100%"})`
      : `translateX(${isTransitioning ? "0" : "100%"})`;

  const panelStyle = {
    ...panelCommon,
    [side]: 0,
    transform: transformStyle,
  };

  const backdropStyle = {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,.4)",
    transition: "opacity 0.3s ease-out",
    opacity: isTransitioning ? 1 : 0,
  };

  const node = (
    <div
      className="chat-overlay"
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 100000 }}
    >
      <div
        className="chat-overlay__backdrop"
        onClick={backdropClosable ? onClose : undefined}
        style={backdropStyle}
      />
      <aside
        className="chat-overlay__panel"
        style={panelStyle}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className="chat-overlay__content" style={{ padding: 0, overflow: "auto", flex: 1 }}>
          {children}
        </div>
      </aside>
    </div>
  );

  return createPortal(node, document.body);
}

// ------- helpers -------
function getWidth(widths) {
  if (typeof window === "undefined") return widths.mobile;
  const w = window.innerWidth;
  if (w >= 1440) return widths.large;
  if (w >= 1024) return widths.desktop;
  if (w >= 768) return widths.tablet;
  return widths.mobile;
}